import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { Transition } from '@atlaskit/motion';
import type { MinimizableModalView } from '../../types.tsx';
import { useMinimizableModal } from '../use-minimizable-modal/index.tsx';
import { TransitionState } from '../use-minimizable-modal/types.tsx';

export const useTransitionView = (view: MinimizableModalView, renderCondition: boolean) => {
	const { setTransitionState, isTransitionInState } = useMinimizableModal();

	const shouldRenderView = useMemo(() => {
		if (view === 'modal')
			return renderCondition && isTransitionInState(['mini', 'minimized'], TransitionState.EXITED);
		if (view === 'mini')
			return renderCondition && isTransitionInState(['modal', 'minimized'], TransitionState.EXITED);
		if (view === 'minimized')
			return renderCondition && isTransitionInState(['mini', 'modal'], TransitionState.EXITED);
		if (view === 'fullscreen')
			return renderCondition && isTransitionInState([], TransitionState.EXITED);

		return false;
	}, [renderCondition, isTransitionInState, view]);

	// SlideIn onFinish prop is sometimes called before the useEffect
	// isAnimationComplete is used to control that
	const isAnimationComplete = useRef<boolean>(false);
	const isInitialised = useRef<boolean>(false);

	useEffect(() => {
		if (isInitialised.current) {
			isAnimationComplete.current = false;
		}
	}, [shouldRenderView]);

	useEffect(() => {
		if (shouldRenderView && !isAnimationComplete.current) {
			isInitialised.current = true;
			setTransitionState(view, TransitionState.TRANSITIONING);
		} else if (!shouldRenderView && !isAnimationComplete.current && isInitialised.current) {
			setTransitionState(view, TransitionState.TRANSITIONING);
		}
	}, [shouldRenderView, setTransitionState, view]);

	const onFinishAnimation = useCallback(
		(state: Transition) => {
			isAnimationComplete.current = true;
			if (state === 'entering') {
				setTransitionState(view, TransitionState.ENTERED);
			}
			if (state === 'exiting') {
				setTransitionState(view, TransitionState.EXITED);
			}
		},
		[setTransitionState, view],
	);

	return {
		onFinishAnimation,
		shouldRenderView,
	};
};
